import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import * as _ from 'lodash';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MultiselectEvent, MultiselectItem } from '../../multiselect/multiselect.component';
import { FilterOperatorType } from '../models/filter-operator-type';
import { FilterGroup, FilterUIType, ODataFilter, ODataFilterValues } from '../models/odata-filter';
import { OPERATORS } from '../models/operators';
import { SearchValueType } from '../models/search-value-type';
import { SelectedFilter } from '../models/selected-filter';

@Component({
  selector: 'odata-filter-item',
  templateUrl: './odata-filter-item.component.html',
  styleUrls: ['./odata-filter-item.component.scss'],
})
export class ODataFilterItemComponent implements OnInit {
  @Input() selectedFilter: SelectedFilter;
  @Input() filters: ODataFilter[];
  @Output() delete = new EventEmitter();
  @Output() addSubFilter = new EventEmitter();

  groups: FilterOptionGroup[] = [];
  myControl = new UntypedFormControl();
  filteredOptions: Observable<FilterOptionGroup[]>;

  get operators() {
    return OPERATORS[this.selectedFilter.filter.valueType].operators;
  }

  constructor() {}

  ngOnInit(): void {
    this.groups = this.makeGroupOfFilters(this.filters);
    if (this.selectedFilter.filter) {
      this.myControl.setValue(this.selectedFilter.filter);
      this.setMultiselectItems();
    }

    this.filteredOptions = this.myControl.valueChanges.pipe(
      startWith(''),
      map((value) => (typeof value === 'string' ? value : value.displayName)),
      map((displayName) => (displayName ? this.filter(displayName) : this.groups.slice()))
    );
  }

  get inputType() {
    if (this.selectedFilter.filter.valueType == SearchValueType.Number) return 'number';
    if (
      this.selectedFilter.filter.valueType == SearchValueType.Date ||
      this.selectedFilter.filter.valueType == SearchValueType.DateRange
    )
      return 'date';

    return 'text';
  }
  get uiType() {
    return FilterUIType;
  }

  multiselectItems: MultiselectItem[] = [];
  get listItems(): string[] {
    return (
      this.selectedFilter.filterOperator.defaultSearchValues ?? this.selectedFilter.filter.values
    );
  }
  get selectedMultiselectItems(): MultiselectItem[] {
    return this.selectedFilter.searchValues.map((v) => new MultiselectItem(v.searchValue, v.name));
  }

  get textBoxPlaceholder(): string {
    return this.selectedFilter.uiType == FilterUIType.Textbox &&
      this.selectedFilter.filterOperator.actualOperator == FilterOperatorType.in
      ? `Use ';' to separate emails...`
      : 'Search here...';
  }

  get showAddConditionButton(): boolean {
    return (
      this.selectedFilter &&
      this.selectedFilter.filterOperator &&
      this.selectedFilter.uiType == FilterUIType.Multiselect &&
      this.selectedFilter.filterOperator.displayOperator != FilterOperatorType.isEmpty &&
      this.selectedFilter.hasSubFilter &&
      !this.selectedFilter.isSubFilterAdded
    );
  }

  displayFn(filter: ODataFilter): string {
    return filter && filter.displayName ? filter.displayName : '';
  }

  onDelete() {
    this.delete.emit();
  }
  setMultiselectItems() {
    const values =
      this.selectedFilter.filterOperator.defaultSearchValues ?? this.selectedFilter.filter.values;
    this.multiselectItems = values?.map((v) => new MultiselectItem(v.searchValue, v.name));
  }
  multiselectSelectionChanged(event: MultiselectEvent) {
    this.selectedFilter.isFilterApplied = false;
    this.selectedFilter.searchValues = event.selectedItems.map(
      (x) => new ODataFilterValues(x.id, x.name)
    );
  }

  autocompleteSelectedChanged(filter) {
    this.selectedFilter.filter = filter;
    this.selectedFilter.filterOperator = this.operators[0];
    this.selectedFilter.isFilterApplied = false;
    this.setMultiselectItems();
    this.resetSelectedFilterSearchValues();
  }

  resetSelectedFilterSearchValues() {
    this.selectedFilter.searchValue = '';
    this.selectedFilter.searchValues = [];
    this.selectedFilter.selectedSubFilters = [];
  }
  onAddSubFilter() {
    this.addSubFilter.emit();
  }
  onOperatorsSelectionChange() {
    this.selectedFilter.isFilterApplied = false;
    if (
      !this.selectedFilter.filterOperator ||
      this.selectedFilter.filterOperator.displayOperator == FilterOperatorType.isEmpty
    ) {
      this.selectedFilter.selectedSubFilters = [];
    }
  }
  private makeGroupOfFilters(odataFilters: ODataFilter[]): FilterOptionGroup[] {
    let groups = [];
    let systemFields = odataFilters.filter(
      (x) =>
        x.filterGroup == FilterGroup.SystemFields || x.filterGroup == FilterGroup.SystemChildFields
    );

    if (systemFields.length) {
      groups = [
        {
          name: 'System Fields',
          filters: systemFields,
        },
      ];
    }

    groups = groups.concat(
      _.chain(
        odataFilters.filter(
          (x) =>
            x.filterGroup != FilterGroup.SystemFields &&
            x.filterGroup != FilterGroup.SystemChildFields
        )
      )
        .groupBy('filterGroup')
        .map((value, key) => ({ name: key, filters: value }))
        .value()
    );
    return groups;
  }

  private filter(name: string): FilterOptionGroup[] {
    const filterValue = name.toLowerCase();
    let filters = this.filters.filter(
      (option) => option.displayName.toLowerCase().indexOf(filterValue) != -1
    );
    return this.makeGroupOfFilters(filters);
  }
}

export interface FilterOptionGroup {
  name: string;
  filters: ODataFilter[];
}
